import type { Slide } from '../types/slide';
import { buildLayout } from './layouts';

export interface CompanyTemplate {
  id: string;
  title: string;
  description: string;
  theme: string;
  /** Built-in templates ship in the client; custom ones come from the API. */
  builtin: boolean;
  slides: Slide[];
}

function leadershipWeekly(): Slide[] {
  return [
    buildLayout('title_slide', 0, {
      title: 'Weekly Leadership Update',
      subtitle: 'Delivery · Ops · Security',
    }),
    buildLayout('agenda', 1, {
      title: 'Agenda',
      bullets: ['Outcomes this week', 'Risks & asks', 'Next week focus'],
    }),
    buildLayout('kpi_3_card', 2, {
      title: 'This week at a glance',
      kpis: [
        { metric: '—', label: 'On-time delivery', delta: '' },
        { metric: '—', label: 'Incidents', delta: '' },
        { metric: '—', label: 'Open risks', delta: '' },
      ],
    }),
    buildLayout('exec_summary', 3, {
      title: 'What moved',
      bullets: ['Replace with the week’s top outcomes', 'Call out one customer or stakeholder win', 'Flag one item that slipped and why'],
    }),
    buildLayout('two_column_split', 4, {
      leftTitle: 'Risks',
      leftBullets: ['Risk 1 — owner & mitigation', 'Risk 2 — owner & mitigation'],
      rightTitle: 'Asks',
      rightBullets: ['Decision needed', 'Support or capacity ask'],
    }),
    buildLayout('closing', 5, { title: 'Thank you', subtitle: 'Questions?' }),
  ];
}

function projectKickoff(): Slide[] {
  return [
    buildLayout('title_slide', 0, {
      title: 'Project kickoff',
      subtitle: 'Scope · Success · Plan',
    }),
    buildLayout('exec_summary', 1, {
      title: 'Why this matters',
      bullets: ['Business outcome in one line', 'Who is impacted', 'What “done” looks like'],
    }),
    buildLayout('three_column', 2, {
      title: 'Success criteria',
      leftTitle: 'Outcome',
      leftBullets: ['Measurable result'],
      midTitle: 'Quality',
      midBullets: ['Brand / compliance bar'],
      rightTitle: 'Timing',
      rightBullets: ['Target date'],
    }),
    buildLayout('timeline_roadmap', 3, {
      title: 'Milestones',
      milestones: [
        { date: 'Week 1', title: 'Discover', detail: 'Align on scope' },
        { date: 'Week 3', title: 'Build', detail: 'First working cut' },
        { date: 'Week 5', title: 'Launch', detail: 'Go-live checklist' },
      ],
    }),
    buildLayout('two_column_split', 4, {
      leftTitle: 'In scope',
      leftBullets: ['Item A', 'Item B'],
      rightTitle: 'Out of scope',
      rightBullets: ['Item C', 'Item D'],
    }),
    buildLayout('closing', 5, { title: 'Thank you', subtitle: 'Next step: confirm owners' }),
  ];
}

function departmentUpdate(): Slide[] {
  return [
    buildLayout('title_slide', 0, {
      title: 'Department update',
      subtitle: 'Priorities · Capacity · Asks',
    }),
    buildLayout('section_divider', 1, { title: 'Priorities' }),
    buildLayout('exec_summary', 2, {
      title: 'Focus this month',
      bullets: ['Priority 1', 'Priority 2', 'Priority 3'],
    }),
    buildLayout('chart_and_takeaways', 3, {
      title: 'Capacity snapshot',
      chartType: 'bar',
      chartLabels: ['Run', 'Change', 'Improve'],
      chartValues: [40, 35, 25],
      takeaways: ['Protect run capacity', 'One improvement bet', 'Ask if change load spikes'],
    }),
    buildLayout('quote_highlight', 4, {
      quote: 'One sentence the leadership team should remember.',
      attribution: '— Your team',
    }),
    buildLayout('closing', 5, { title: 'Thank you', subtitle: 'Open discussion' }),
  ];
}

/** Curated starters every AST manager can use without admin setup. */
export const BUILTIN_TEMPLATES: CompanyTemplate[] = [
  {
    id: 'builtin_leadership_weekly',
    title: 'Weekly leadership update',
    description: 'Agenda, KPIs, outcomes, risks & asks — for recurring exec syncs.',
    theme: 'ast_standard',
    builtin: true,
    slides: leadershipWeekly(),
  },
  {
    id: 'builtin_project_kickoff',
    title: 'Project kickoff',
    description: 'Why, success criteria, milestones, and in/out of scope.',
    theme: 'ast_standard',
    builtin: true,
    slides: projectKickoff(),
  },
  {
    id: 'builtin_department_update',
    title: 'Department update',
    description: 'Monthly priorities, capacity chart, and a memorable takeaway.',
    theme: 'ast_standard',
    builtin: true,
    slides: departmentUpdate(),
  },
];

export function getBuiltinTemplate(id: string): CompanyTemplate | undefined {
  return BUILTIN_TEMPLATES.find((t) => t.id === id);
}
